import { object, string } from 'yup';

import { FIELD_NAMES } from './constants';
import { commonMessages, commonRequiredValidationErrors, requiredValidationErrorsByCountry, validationTooltipsByCountry } from './messages';
import { buildMessageCallback } from '../../validation/utilities';

const { CITY, COMPANY, COUNTRY_CODE, EMAIL, LINE1, LINE2, LINE3, NAME, PHONE, POSTAL_CODE, STATE, RESIDENTIAL_INDICATOR } = FIELD_NAMES;

export const optionalFieldsByCountryCode = {
  NZ: [STATE, COMPANY],
  FR: [STATE, COMPANY],
  DE: [STATE, COMPANY],
  GB: [STATE, COMPANY],
};

export const getRequiredMessageDescriptorForField = (countryCode, fieldName) => {
  return (
    requiredValidationErrorsByCountry[countryCode]?.[fieldName] ||
    commonRequiredValidationErrors[fieldName] ||
    commonRequiredValidationErrors.required
  );
};

export const isAddressFieldOptional = (countryCode, fieldSchema) => {
  const fieldSchemaDescription = fieldSchema.describe();
  const { fieldName } = fieldSchemaDescription.meta;

  const tooltipDescriptor = validationTooltipsByCountry[countryCode]?.[fieldName];
  const messageDescriptor = getRequiredMessageDescriptorForField(countryCode, fieldName);

  return optionalFieldsByCountryCode[countryCode]?.includes(fieldName)
    ? fieldSchema.nullable()
    : fieldSchema.required(buildMessageCallback(messageDescriptor, { tooltipDescriptor }));
};

export const buildMetadata = (fieldName) => {
  return { fieldName };
};

/**
 * Determines if a field is required based on country code and external requirements
 * @param {string} fieldName - The field name from FIELD_NAMES
 * @param {string} countryCode - The country code (e.g., 'US', 'NZ')
 * @param {boolean} externallyRequired - Whether the field is externally required (e.g., ship-from requirements)
 * @param {Array<string>} overrideRequiredFields - Optional array of field names to use instead of standard required fields
 * @returns {boolean} - Whether the field should be marked as required
 */
export const computeFieldRequiredState = (fieldName, countryCode, externallyRequired = false, overrideRequiredFields = null) => {
  // If overrideRequiredFields is provided, use it instead of standard required fields
  if (overrideRequiredFields !== null) {
    return overrideRequiredFields.includes(fieldName);
  }

  // All standard address fields that are required by default (unless marked optional for specific countries)
  const standardRequiredFields = [
    FIELD_NAMES.NAME,
    FIELD_NAMES.LINE1,
    FIELD_NAMES.CITY,
    FIELD_NAMES.STATE,
    FIELD_NAMES.POSTAL_CODE,
    FIELD_NAMES.PHONE,
  ];

  const optionalFieldsForCountry = optionalFieldsByCountryCode[countryCode] || [];

  // Field is required if:
  // 1. It's in standardRequiredFields AND not marked optional for this country, OR
  // 2. It's marked as externallyRequired (like PHONE, COMPANY for ship-from)
  const isFieldRequiredBySchema = standardRequiredFields.includes(fieldName) && !optionalFieldsForCountry.includes(fieldName);

  return isFieldRequiredBySchema || externallyRequired;
};

export const addressSchema = object({
  [CITY]: string().trim().meta(buildMetadata(CITY)).when(COUNTRY_CODE, isAddressFieldOptional),
  [COMPANY]: string().nullable().trim().meta(buildMetadata(COMPANY)).max(50, buildMessageCallback(commonMessages.companyLengthLimit)), // capped at 50 in the DB.,
  [COUNTRY_CODE]: string().meta(buildMetadata(COUNTRY_CODE)).required(),
  [EMAIL]: string().email(buildMessageCallback(commonMessages.emailFormatValidationError)).meta(buildMetadata(EMAIL)).nullable(),
  [LINE1]: string().trim().meta(buildMetadata(LINE1)).when(COUNTRY_CODE, isAddressFieldOptional),
  [LINE2]: string().meta(buildMetadata(LINE2)).nullable(),
  [LINE3]: string().meta(buildMetadata(LINE3)).nullable(),
  [NAME]: string().trim().meta(buildMetadata(NAME)).when(COUNTRY_CODE, isAddressFieldOptional),
  [PHONE]: string().trim().meta(buildMetadata(PHONE)).when(COUNTRY_CODE, isAddressFieldOptional),
  [POSTAL_CODE]: string().trim().meta(buildMetadata(POSTAL_CODE)).when(COUNTRY_CODE, isAddressFieldOptional),
  [RESIDENTIAL_INDICATOR]: string().meta(buildMetadata(RESIDENTIAL_INDICATOR)),
  [STATE]: string().meta(buildMetadata(STATE)).when(COUNTRY_CODE, isAddressFieldOptional),
});
